import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  confirmTelegramAccount, createTelegramLink, disconnectTelegram, getNotificationPreferences,
  saveNotificationPreferences, telegramLinkUrl, NOTIFICATION_PREFERENCES_KEY, type NotificationChoices,
  type NotificationPreferencesResponse, type TelegramLink,
} from "@/api/notifications";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

const QUERY_KEY = NOTIFICATION_PREFERENCES_KEY;
function choices(data: NotificationPreferencesResponse): NotificationChoices {
  return {
    email_enabled: data.email_enabled, telegram_enabled: data.telegram_enabled,
    daily_report: data.daily_report, daily_plan: data.daily_plan, weekly_plan: data.weekly_plan,
    delivery_hour: data.delivery_hour,
  };
}

interface PreferenceForm {
  source: NotificationPreferencesResponse;
  draft: NotificationChoices;
  reviewNeeded: boolean;
}

/** Refresh untouched fields; a new recipient always needs a new channel choice. */
function reconcile(previous: PreferenceForm | null, source: NotificationPreferencesResponse): PreferenceForm {
  if (previous?.source === source) return previous;
  const draft = choices(source);
  if (!previous) return { source, draft, reviewNeeded: false };
  for (const key of Object.keys(draft) as (keyof NotificationChoices)[]) {
    if (previous.draft[key] !== previous.source[key]) Object.assign(draft, { [key]: previous.draft[key] });
  }
  const bindingChanged = previous.source.telegram_binding_revision !== source.telegram_binding_revision;
  const reviewNeeded = previous.reviewNeeded || bindingChanged;
  if (reviewNeeded) draft.telegram_enabled = false;
  return { source, draft, reviewNeeded };
}

type Operation = "save" | "link" | "confirm" | "disconnect" | "refresh";
type Notice = "saved" | "connected" | "disconnected" | "cancelled";

export function NotificationPreferences() {
  const { t, i18n } = useTranslation();
  const token = useAuthStore(state => state.token);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: QUERY_KEY, queryFn: ({ signal }) => getNotificationPreferences(signal), refetchOnWindowFocus: false });
  const [form, setForm] = useState<PreferenceForm | null>(null);
  const [bindingStale, setBindingStale] = useState(false);
  const [link, setLink] = useState<TelegramLink | null>(null);
  const [linkExpired, setLinkExpired] = useState(false);
  const [busy, setBusy] = useState<Operation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const mounted = useRef(true);
  const inflight = useRef(false);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controller.current?.abort(); };
  }, []);
  useEffect(() => {
    if (!link) { setLinkExpired(false); return; }
    const remaining = new Date(link.expires_at).getTime() - Date.now();
    setLinkExpired(remaining <= 0);
    const timer = setTimeout(() => setLinkExpired(true), Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [link]);
  useEffect(() => { if (q.data) setForm(previous => reconcile(previous, q.data)); }, [q.data]);

  const data = q.data;
  const currentForm = data ? reconcile(form, data) : null;
  const draft = currentForm?.draft;
  const telegramConfirmed = data?.telegram_connected === true && !data.telegram_pending &&
    !!data.telegram_binding_revision && !bindingStale;
  const readOnly = data?.readonly_demo === true;
  const dirty = !!data && !!draft && JSON.stringify(draft) !== JSON.stringify(choices(data));
  const locked = !!busy || readOnly;

  async function perform<T>(operation: Operation, request: (signal: AbortSignal) => Promise<T>, accept: (result: T) => void) {
    if (inflight.current || !data || readOnly) return;
    const active = new AbortController();
    controller.current = active;
    inflight.current = true;
    setBusy(operation); setError(null); setNotice(null);
    const current = () => mounted.current && !active.signal.aborted && useAuthStore.getState().token === token;
    try {
      await qc.cancelQueries({ queryKey: QUERY_KEY });
      if (!current()) return;
      const result = await request(active.signal);
      if (current()) accept(result);
    } catch (caught) {
      if (current()) {
        const code = (caught as { response?: { data?: { detail?: { code?: string } } } } | null)?.response?.data?.detail?.code;
        if (operation === "save" && ["telegram_binding_changed", "telegram_not_connected", "telegram_confirmation_required"].includes(code ?? "")) {
          setBindingStale(true);
          setForm(previous => {
            const next = reconcile(previous, data);
            return { ...next, draft: { ...next.draft, telegram_enabled: false }, reviewNeeded: true };
          });
          setError("binding_changed");
        } else setError(operation === "save" ? "save_failed" : operation === "refresh" ? "refresh_failed" : operation === "confirm" ? "confirm_failed" : "telegram_failed");
      }
    } finally {
      if (current()) { inflight.current = false; setBusy(null); }
    }
  }

  function updateData(next: NotificationPreferencesResponse) {
    qc.setQueryData(QUERY_KEY, next);
    setForm(previous => reconcile(previous, next));
    setBindingStale(false);
  }
  function change<K extends keyof NotificationChoices>(key: K, value: NotificationChoices[K]) {
    if (!data) return;
    setForm(previous => {
      const next = reconcile(previous, data);
      return { ...next, draft: { ...next.draft, [key]: value }, reviewNeeded: key === "telegram_enabled" ? false : next.reviewNeeded };
    });
    setError(null); setNotice(null);
  }
  function save(event: FormEvent) {
    event.preventDefault();
    if (!draft || !dirty || (draft.telegram_enabled && !telegramConfirmed)) return;
    const payload = { ...draft, ...(draft.telegram_enabled ? { telegram_binding_revision: currentForm!.source.telegram_binding_revision! } : {}) };
    void perform("save", signal => saveNotificationPreferences(payload, signal), next => {
      updateData(next); setForm({ source: next, draft: choices(next), reviewNeeded: false }); setNotice("saved");
    });
  }
  function beginLink() {
    void perform("link", createTelegramLink, next => {
      if (!telegramLinkUrl(next.url) || !Number.isFinite(new Date(next.expires_at).getTime())) {
        setError("telegram_failed"); return;
      }
      setLink(next);
    });
  }
  function disconnect() {
    const wasConnected = data?.telegram_connected === true;
    void perform("disconnect", disconnectTelegram, next => {
      updateData(next); setLink(null);
      setForm(previous => ({ ...reconcile(previous, next), draft: { ...reconcile(previous, next).draft, telegram_enabled: false }, reviewNeeded: false }));
      setNotice(wasConnected ? "disconnected" : "cancelled");
    });
  }
  function confirm() {
    if (!data?.telegram_pending || !data.telegram_confirmation_id || !data.telegram_display_name) return;
    const confirmationId = data.telegram_confirmation_id;
    void perform("confirm", signal => confirmTelegramAccount(confirmationId, signal), next => {
      updateData(next); setLink(null); setNotice("connected");
      setForm(previous => ({ ...reconcile(previous, next), draft: { ...reconcile(previous, next).draft, telegram_enabled: false }, reviewNeeded: false }));
      // Connecting an identity never opts the athlete into notifications.
    });
  }

  if (q.isPending || (q.data && !draft)) return <Card aria-busy="true"><h2 className="font-serif text-xl">{t("notifications.title")}</h2><p role="status" className="mt-3 text-sm text-taupe">{t("notifications.loading")}</p></Card>;
  if (!data || !draft) return <Card><h2 className="font-serif text-xl">{t("notifications.title")}</h2><p role="alert" className="mt-3 text-sm text-coral">{t("notifications.load_failed")}</p><Button type="button" size="sm" className="mt-3" onClick={() => q.refetch()} disabled={q.isFetching}>{t("common.retry")}</Button></Card>;

  const hasPendingIdentity = data.telegram_pending && !!data.telegram_confirmation_id && !!data.telegram_display_name;
  const linkValid = link && !linkExpired;
  let expiry = "";
  if (link) {
    try { expiry = new Intl.DateTimeFormat(i18n.language, { timeZone: data.timezone, hour: "2-digit", minute: "2-digit" }).format(new Date(link.expires_at)); }
    catch { expiry = new Date(link.expires_at).toISOString().slice(11, 16) + " UTC"; }
  }

  return <Card id="notifications" aria-busy={!!busy}>
    <h2 className="font-serif text-xl text-navy-deep">{t("notifications.title")}</h2>
    <p className="mt-2 text-sm text-taupe">{t("notifications.description")}</p>
    <p className="mt-2 text-sm text-taupe">{t("notifications.existing_content")}</p>
    {readOnly && <p role="status" className="mt-3 rounded-lg bg-cream p-3 text-sm text-taupe">{t("notifications.demo")}</p>}

    <form className="mt-5 space-y-5" onSubmit={save}>
      <fieldset disabled={locked} className="space-y-3">
        <legend className="mb-2 font-medium text-navy-deep">{t("notifications.channels")}</legend>
        <Choice label={t("notifications.email")} checked={draft.email_enabled} onChange={value => change("email_enabled", value)} />
        <p className="pl-7 text-sm text-taupe">{t("notifications.email_to", { email: data.email })}</p>
        <Choice label="Telegram" checked={draft.telegram_enabled} onChange={value => change("telegram_enabled", value)}
          disabled={!draft.telegram_enabled && (!telegramConfirmed || !data.telegram_available)} />
        <p className="pl-7 text-sm text-taupe">{t(telegramConfirmed ? "notifications.telegram_choose" : "notifications.telegram_connect_first")}</p>
      </fieldset>

      <fieldset disabled={locked} className="space-y-3">
        <legend className="mb-2 font-medium text-navy-deep">{t("notifications.content")}</legend>
        <Choice label={t("notifications.daily_report")} checked={draft.daily_report} onChange={value => change("daily_report", value)} />
        <Choice label={t("notifications.daily_plan")} checked={draft.daily_plan} onChange={value => change("daily_plan", value)} />
        <Choice label={t("notifications.weekly_plan")} checked={draft.weekly_plan} onChange={value => change("weekly_plan", value)} />
      </fieldset>

      <div>
        <label htmlFor="notification-delivery-hour" className="block font-medium text-navy-deep">{t("notifications.delivery_hour")}</label>
        <select id="notification-delivery-hour" value={draft.delivery_hour} disabled={locked}
          onChange={event => change("delivery_hour", Number(event.target.value))}
          className="mt-2 rounded-lg border border-cream-border bg-cream px-3 py-2 text-sm text-navy-deep">
          {Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{String(hour).padStart(2, "0")}:00</option>)}
        </select>
        <p className="mt-2 text-sm text-taupe">{t("notifications.timezone", { timezone: data.timezone })}</p>
        <p className="mt-2 text-sm text-taupe">{t("notifications.schedule")}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" loading={busy === "save"} disabled={locked || !dirty || (draft.telegram_enabled && !telegramConfirmed)}>{t("notifications.save")}</Button>
        {dirty && <Button type="button" variant="ghost" size="sm" disabled={locked} onClick={() => {
          const next = choices(data);
          if (currentForm?.reviewNeeded) next.telegram_enabled = false;
          setForm({ source: data, draft: next, reviewNeeded: currentForm?.reviewNeeded ?? false }); setError(null); setNotice(null);
        }}>{t("notifications.discard")}</Button>}
      </div>
    </form>

    <section className="mt-5 border-t border-cream-border pt-4" aria-label={t("notifications.telegram_account")}>
      <h3 className="font-medium text-navy-deep">{t("notifications.telegram_account")}</h3>
      {!data.telegram_available && <p className="mt-2 text-sm text-taupe">{t("notifications.telegram_unavailable")}</p>}
      {data.telegram_connected && !hasPendingIdentity && <p className="mt-2 text-sm text-navy-deep">{t("notifications.connected_as", { name: data.telegram_display_name ?? "Telegram" })}</p>}
      {hasPendingIdentity ? <div className="mt-3 space-y-3 rounded-lg border border-amber/40 bg-amber/10 p-3">
        <p className="text-sm text-navy-deep">{t("notifications.confirm_identity")}</p>
        <p className="font-medium text-navy-deep">{data.telegram_display_name}</p>
        <p className="text-sm text-taupe">{t("notifications.confirm_help")}</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" disabled={locked} loading={busy === "confirm"} onClick={confirm}>{t("notifications.confirm")}</Button>
          <Button type="button" size="sm" variant="ghost" disabled={locked} onClick={disconnect}>{t("notifications.decline")}</Button>
        </div>
      </div> : link ? <div className="mt-3 space-y-3 text-sm">
        <p className="text-taupe">{t("notifications.telegram_steps")}</p>
        {linkValid && telegramLinkUrl(link.url) ? <a href={telegramLinkUrl(link.url)!} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" className="inline-block font-medium text-amber-dark underline">{t("notifications.open_telegram")}</a> : <p role="status" className="text-taupe">{t("notifications.link_expired")}</p>}
        {linkValid && <p className="text-taupe">{t("notifications.link_expires", { time: expiry })}</p>}
        <div className="flex flex-wrap gap-2">
          {!linkValid && <Button type="button" size="sm" disabled={locked || !data.telegram_available} onClick={beginLink}>{t("notifications.new_link")}</Button>}
          <Button type="button" size="sm" variant="ghost" disabled={locked} onClick={disconnect}>{t("notifications.cancel_connection")}</Button>
        </div>
      </div> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {!data.telegram_connected && !hasPendingIdentity && !link && <Button type="button" size="sm" disabled={locked || !data.telegram_available} loading={busy === "link"} onClick={beginLink}>{t("notifications.connect")}</Button>}
        <Button type="button" size="sm" variant="ghost" disabled={locked} loading={busy === "refresh"} onClick={() => void perform("refresh", getNotificationPreferences, updateData)}>{t("notifications.refresh")}</Button>
        {data.telegram_connected && !hasPendingIdentity && <Button type="button" size="sm" variant="ghost" disabled={locked} loading={busy === "disconnect"} onClick={disconnect}>{t("notifications.disconnect")}</Button>}
      </div>
    </section>
    {currentForm?.reviewNeeded && error !== "binding_changed" && <p role="alert" className="mt-3 text-sm text-coral">{t("notifications.binding_changed")}</p>}
    {q.isError && <p role="alert" className="mt-3 text-sm text-coral">{t("notifications.load_failed")}</p>}
    {error && <p role="alert" className="mt-3 text-sm text-coral">{t(`notifications.${error}`)}</p>}
    {notice && <p role="status" className="mt-3 text-sm text-navy-deep">{t(`notifications.${notice}`)}</p>}
    <p className="mt-4 text-sm text-taupe">{t("notifications.opt_out")}</p>
  </Card>;
}

function Choice({ label, checked, onChange, disabled = false }: { label: string; checked: boolean; onChange: (value: boolean) => void; disabled?: boolean }) {
  return <label className="flex items-start gap-3 text-sm text-navy-deep">
    <input type="checkbox" checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} className="mt-0.5 h-4 w-4 accent-amber" />
    <span>{label}</span>
  </label>;
}

export function NotificationPreferencesLink() {
  const { t } = useTranslation();
  return <Link to="/account/notifications" className="ml-4 inline-block py-2 text-sm text-amber-dark underline">{t("notifications.title")}</Link>;
}
